/**
 * Whether AI tools were used to make a product, as its seller declares it.
 *
 * THE RULE (owner decisions 2026-10-22, for every product): AI-assisted work
 * is allowed, with a clear disclosure; work generated by AI is not.
 * Every listing answers one of the two below, and the product page shows the
 * label beside the title when AI assisted, so a buyer who wants work made
 * without it can tell before buying. There is deliberately no third answer:
 * a product that AI made rather than assisted is not allowed on supa.place.
 * The guidelines' "AI-Generated Content Disclosure" section is the policy
 * text; keep it and these answers saying the same.
 *
 * STORED as `aiUse` on products/{id}; firestore.rules accepts only these
 * values (aiUses(), compared with AI_USE_VALUES by ai-disclosure.test.ts).
 * Required before a product goes live (the finalize dialog's `aiUse` issue).
 * A product saved before 2026-10-22 has none and shows no label until its
 * seller next saves it, which the same check makes them answer.
 */

export const AI_USE_VALUES = ['none', 'assisted'] as const;

export type AiUse = (typeof AI_USE_VALUES)[number];

export type AiUseCopy = {
  /** The option in the listing form. */
  option: string;
  /** Under the option: what picking it says. */
  hint: string;
  /** The label buyers see. */
  label: string;
  /** The label's explanation for buyers. */
  detail: string;
};

export const AI_USE_COPY: Readonly<Record<AiUse, AiUseCopy>> = {
  none: {
    option: 'No AI',
    hint: 'I made all of it without AI tools.',
    label: 'No AI used',
    detail: 'The seller says no AI tools were used to make this.',
  },
  assisted: {
    option: 'AI-assisted',
    hint: 'I made it, and used AI tools to help with parts of it (ideas, text or images). I wrote, edited and checked the result myself.',
    label: 'AI-assisted',
    detail: 'The seller made this with help from AI tools for parts of it, and wrote, edited and checked it.',
  },
};

/**
 * The same answers for a resale pack (the Resell rights purpose), which its
 * seller usually did not make, so they answer as far as they know (owner
 * decision 2026-11-02): the rule covers resale packs like any product, and a
 * seller who knows AI generated one cannot list it. The stored values and the
 * label stay the same; only the words around them change.
 */
export const AI_USE_RESALE_COPY: Readonly<Record<AiUse, AiUseCopy>> = {
  none: {
    option: 'No AI',
    hint: 'As far as I know, no AI tools were used to make this pack.',
    label: 'No AI used',
    detail: 'The seller says that, as far as they know, no AI tools were used to make this pack.',
  },
  assisted: {
    option: 'AI-assisted',
    hint: 'AI tools helped with parts of this pack (ideas, text or images), and a person wrote, edited and checked the result.',
    label: 'AI-assisted',
    detail: 'The seller says AI tools helped with parts of this pack, and a person wrote, edited and checked it.',
  },
};

/** The answers' words for a product its seller made, or for a resale pack. */
export function aiUseCopyOf(resalePack: boolean): Readonly<Record<AiUse, AiUseCopy>> {
  return resalePack ? AI_USE_RESALE_COPY : AI_USE_COPY;
}

/** The question above the answers in the listing forms. */
export function aiUseQuestion(resalePack: boolean): string {
  return resalePack
    ? 'As far as you know, how much AI went into making this pack? Required to launch.'
    : 'How much AI went into making this product. Required to launch.';
}

/** Why there is no "AI-generated" answer, for the listing form. */
export const AI_GENERATED_REFUSED =
  'Products generated by AI are not allowed on supa.place. AI can help you make your product, not make it for you.';

/** The same, said to the seller of a resale pack. */
export function aiGeneratedRefused(resalePack: boolean): string {
  return resalePack
    ? 'Products generated by AI are not allowed on supa.place, resale packs included. If you know AI made this pack, rather than helped make it, it cannot be listed here.'
    : AI_GENERATED_REFUSED;
}

/**
 * The report dialog's reason for a listing the rule removes: generated by AI,
 * or labelled No AI when AI helped (the guidelines' takedown ground).
 */
export const AI_REPORT_REASON = 'Generated by AI, or AI use not labelled';

export function isAiUse(value: unknown): value is AiUse {
  return typeof value === 'string' && (AI_USE_VALUES as readonly string[]).includes(value);
}

/** A stored value as an answer, or null for none (a product saved before the field). */
export function aiUseOf(value: unknown): AiUse | null {
  return isAiUse(value) ? value : null;
}

/** Whether the answer puts a label beside the product's title: any AI use does. */
export function showsAiLabel(value: unknown): boolean {
  return aiUseOf(value) === 'assisted';
}
